/**
 * NotificationIdentityVerificationItem
 *
 * identityVerification — `notification_identity_verification_item.dart:25-107`.
 * Leading: full-size `verify.png` icon (no avatar / user tap). Fixed
 * `Identity Verification` title. Description varies by
 * `identityVerificationInfo.status`; the `declined` case appends the
 * user-friendly decline-reason label. Subtitle is `{description} - {date}`.
 * No trailing thumbnail.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { IdentityVerificationStatus } from '@/domain/enums/premiumPackages/IdentityVerificationStatus';
import { IdentityVerificationDeclineReason } from '@/domain/enums/identityVerificationDeclineReason';
import { NotificationRowLayout } from '../NotificationRowLayout';
import { NotificationLeadingIcon } from './shared/NotificationLeadingIcon';
import type { NotificationItemProps } from './notificationItemProps';

export const NotificationIdentityVerificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onItemTap,
}) => {
  const { l10n } = useLocale();
  const { formatDate } = useDateFormatters();
  const n = l10n.inbox.notifications;

  const { info } = notification;
  const verification = info.identityVerificationInfo;
  const status = (verification?.status ?? 0) as IdentityVerificationStatus;

  const declineReasonLabel = (reason: number | undefined): string => {
    const labels = n.identityDeclineReasons;
    switch (reason as IdentityVerificationDeclineReason) {
      case IdentityVerificationDeclineReason.unclearDocument:
        return labels.unclearDocument;
      case IdentityVerificationDeclineReason.unclearLiveness:
        return labels.unclearLiveness;
      case IdentityVerificationDeclineReason.unclearUploadedFiles:
        return labels.unclearUploadedFiles;
      case IdentityVerificationDeclineReason.missmatchUploadedFiles:
        return labels.missmatchUploadedFiles;
      case IdentityVerificationDeclineReason.missmatchUploadedFilesProfile:
        return labels.missmatchUploadedFilesProfile;
      case IdentityVerificationDeclineReason.premiumPackageExpired:
        return labels.premiumPackageExpired;
      case IdentityVerificationDeclineReason.identityDataChanged:
        return labels.identityDataChanged;
      default:
        return '';
    }
  };

  let description: string;
  switch (status) {
    case IdentityVerificationStatus.requested:
      description = n.identityRequested;
      break;
    case IdentityVerificationStatus.inReview:
      description = n.identityInReview;
      break;
    case IdentityVerificationStatus.approved:
      description = n.identityApproved;
      break;
    case IdentityVerificationStatus.declined:
      description = n.identityDeclined.replace(
        '{reason}',
        declineReasonLabel(verification?.declineReason),
      );
      break;
    default:
      description = n.identityRequested;
      break;
  }

  const subtitle = `${description} - ${formatDate(info.timestamp)}`;

  return (
    <NotificationRowLayout
      leading={
        <NotificationLeadingIcon iconPath="/assets/images/verify.png" fillContainer />
      }
      title={n.identityVerificationTitle}
      subtitle={subtitle}
      onItemTap={onItemTap ? () => onItemTap(notification) : undefined}
    />
  );
};
